

import { type ReactNode, useMemo, useState, useEffect } from "react";
import { ExpandableText } from "./expandable-text";

/**
 * Converts markdown-style links to HTML
 * Supports: [link text](https://example.com)
 */
export function convertMarkdownLinksToHtml(text: string): string {
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  return text.replace(
    markdownLinkRegex,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline">$1</a>',
  );
}

/**
 * Parses text content and converts markdown links and plain URLs to HTML
 */
export function parseTextWithLinks(text: string): string {
  // First convert markdown links to prevent double-linking URLs inside markdown syntax
  const withMarkdownLinks = convertMarkdownLinksToHtml(text);

  // Then convert plain URLs (avoiding URLs already inside <a> tags from markdown conversion)
  // Match https://, http://, or www. URLs
  const urlRegex =
    /(https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:[/?#][^\s<)]*)?|www\.[a-zA-Z0-9-]+\.[a-zA-Z0-9.\-_]*(?:[/?#][^\s<)]*)?)/gi;

  const withPlainUrls = withMarkdownLinks.replace(
    urlRegex,
    (...args: unknown[]) => {
      const match = args[0] as string;
      const offset = args[args.length - 2] as number;
      const fullString = args[args.length - 1] as string;

      // Check if this URL is already inside an anchor tag (from markdown conversion)
      const beforeMatch = fullString.substring(
        Math.max(0, offset - 100),
        offset,
      );
      const afterMatch = fullString.substring(
        offset,
        Math.min(fullString.length, offset + match.length + 100),
      );

      // If we find <a before the match and </a> after, skip it
      if (
        beforeMatch.lastIndexOf("<a ") > beforeMatch.lastIndexOf("</a>") &&
        afterMatch.includes("</a>")
      ) {
        return match;
      }

      // Remove trailing punctuation that's not part of the URL
      let cleanUrl = match;
      let trailingPunctuation = "";

      // Handle trailing punctuation
      const punctuationMatch = match.match(/([.,;!?]+)$/);
      if (punctuationMatch) {
        trailingPunctuation = punctuationMatch[1];
        cleanUrl = match.slice(0, -trailingPunctuation.length);
      }

      // Add https:// prefix for www URLs
      const href = cleanUrl.startsWith("www.")
        ? `https://${cleanUrl}`
        : cleanUrl;

      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="underline">${cleanUrl}</a>${trailingPunctuation}`;
    },
  );

  // Convert line breaks to <br> tags
  return withPlainUrls.replace(/\n/g, "<br>");
}

const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "a",
    "strong",
    "em",
    "br",
    "b",
    "i",
    "u",
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
    "div",
    "span",
    "s",
    "sub",
    "sup",
    "mark",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
  ALLOW_DATA_ATTR: false,
};

interface RichTextDescriptionProps {
  content: string | ReactNode | null | undefined;
  className?: string;
  truncate?: boolean;
  truncateLines?: number;
}

export function RichTextDescription({
  content,
  className = "",
  truncate = false,
  truncateLines = 3,
}: RichTextDescriptionProps) {
  const [sanitizedHtml, setSanitizedHtml] = useState<string>("");

  // Parse markdown links and plain URLs (can be done during SSR)
  const parsedHtml = useMemo(() => {
    if (!content || typeof content !== "string") return "";
    return parseTextWithLinks(content);
  }, [content]);

  // Sanitize on client side only using dynamic import
  useEffect(() => {
    if (!parsedHtml) {
      setSanitizedHtml("");
      return;
    }

    import("dompurify").then((DOMPurify) => {
      const sanitized = DOMPurify.default.sanitize(
        parsedHtml,
        DOMPURIFY_CONFIG,
      );
      setSanitizedHtml(sanitized);
    });
  }, [parsedHtml]);

  // If content is a ReactNode (not a string), render it directly
  if (typeof content !== "string" && content) {
    if (truncate) {
      return (
        <ExpandableText lines={truncateLines} className={className}>
          {content}
        </ExpandableText>
      );
    }
    return <span className={className}>{content}</span>;
  }

  if (!sanitizedHtml) return null;

  if (truncate) {
    return (
      <ExpandableText lines={truncateLines} className={className}>
        <div
          className="prose prose-sm max-w-none dark:prose-invert break-words"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      </ExpandableText>
    );
  }

  return (
    <div
      className={`prose prose-sm max-w-none dark:prose-invert break-words ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}
